import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Loader2, GraduationCap, Users, ClipboardCheck, TrendingUp,
  FileText, Calendar, ChevronLeft, ChevronRight
} from "lucide-react";
import { getApiUrl } from "../../config/api";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface Class {
  id: string;
  name: string;
  studentCount?: number;
}

const ClassesPage: React.FC = () => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const fetchClasses = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const dashboardRes = await axios.get(getApiUrl("teacher/dashboard"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const classesData: Class[] = dashboardRes.data.classes || [];
      setClasses(classesData);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load classes");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchClasses();
  }, [fetchClasses, token]);

  const filteredClasses = classes.filter((cls) =>
    cls.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredClasses.length / itemsPerPage);
  const currentClasses = filteredClasses.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleAction = (action: string, classId: string) => {
    switch (action) {
      case "attendance":
        navigate(`/attendance?classId=${classId}`);
        break;
      case "performance":
        navigate(`/viewassessments?classId=${classId}`);
        break;
      case "results":
        navigate(`/addassessments?classId=${classId}`);
        break;
      case "timetable":
        navigate(`/timetable?classId=${classId}`);
        break;
      default:
        break;
    }
  };

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">

        <div className="page-title-section">
          <div>
            <h1>My Class Assesments</h1>
            <p className="page-subtitle">
              Manage your assigned classes and access class-specific features
            </p>
          </div>
        </div>

        <div className="utopia-page-container">
          {loading ? (
            <div className="py-40 text-center">
              <Loader2 className="mx-auto animate-spin text-teal-200 opacity-20" size={64} />
            </div>
          ) : error ? (
            <div className="py-40 text-center">
              <p className="text-rose-500 font-semibold">{error}</p>
            </div>
          ) : currentClasses.length === 0 ? (
            <div className="py-40 text-center text-slate-200">
              <GraduationCap size={100} className="mx-auto mb-8 opacity-5" />
              <p className="font-black uppercase tracking-widest text-slate-300">
                {searchTerm ? "No classes found matching your search." : "No classes assigned."}
              </p>
            </div>
          ) : (
            <>
              <table className="utopia-table">
                <thead>
                  <tr>
                    <th>Class Name</th>
                    <th>Students</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentClasses.map((cls) => (
                    <tr key={cls.id} className="group/row">
                      <td>
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 font-black text-xs shadow-inner group-hover/row:bg-teal-50 group-hover/row:text-teal-600 transition-all">
                            <GraduationCap size={20} />
                          </div>
                          <div className="flex flex-col">
                            <span className="font-black text-slate-800 uppercase tracking-tight text-sm">
                              {cls.name}
                            </span>
                            <span className="text-[9px] text-slate-400 font-mono tracking-widest uppercase">
                              Class ID: {String(cls.id || "").slice(-8)}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Users size={16} className="text-slate-400" />
                          <span className="text-sm font-semibold text-slate-600">
                            {cls.studentCount || 0} students
                          </span>
                        </div>
                      </td>
                      <td className="text-right">
                        <div className="flex justify-end gap-2 flex-wrap">
                          <button
                            className="utopia-btn-secondary h-10 px-4 text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
                            onClick={() => handleAction("attendance", cls.id)}
                            title="Attendance Register"
                          >
                            <ClipboardCheck size={12} /> Attendance
                          </button>
                          <button
                            className="utopia-btn-secondary h-10 px-4 text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
                            onClick={() => handleAction("performance", cls.id)}
                            title="Students Performance"
                          >
                            <TrendingUp size={12} /> Performance
                          </button>
                          <button
                            className="utopia-btn-secondary h-10 px-4 text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
                            onClick={() => handleAction("results", cls.id)}
                            title="Add Results"
                          >
                            <FileText size={12} /> Add Results
                          </button>
                          <button
                            className="utopia-btn-primary h-10 px-4 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-teal-50"
                            onClick={() => handleAction("timetable", cls.id)}
                            title="Timetable"
                          >
                            <Calendar size={12} /> Timetable
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className="utopia-pagination p-8 border-t border-slate-50">
                  <button
                    className="utopia-page-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest mx-6">
                    Page {currentPage} / {totalPages}
                  </span>
                  <button
                    className="utopia-page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ClassesPage;











